/* =========================================================================
   LandingPage6 — Grand Slam Offer landing page for the "From Idea to App"
   course: build your own software with AI, in Spanish, no engineer / no
   English. Structure follows Alex Hormozi's $100M Offers: hero (MAGIC
   headline) → the problem → how it works → offer + price → guarantee →
   final CTA. Copy is English for now (lp6.* keys); the Spanish view falls back
   to it until translated. Everything is scoped under .lp6 so it never collides
   with the Argon globals or the shelved LandingPage5.
   ========================================================================= */

import React, { useEffect } from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import "./LandingPage6.css";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLaptopCode,
  faDollarSign,
  faLanguage,
  faUsers,
  faLightbulb,
  faRobot,
  faHeadphones,
  faRocket,
  faShieldHalved,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import ValueStack, { SHOW_STACK_PRICES } from "components/Offer/ValueStack.js";
import { ENROLLMENT_OPEN } from "../../config/enrollment";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// The fears that keep the idea stuck (Hormozi step 2: list the problems).
const PROBLEMS = [
  { n: 1, icon: faLaptopCode, tone: "lp6-ic-cyan" },
  { n: 2, icon: faDollarSign, tone: "lp6-ic-purple" },
  { n: 3, icon: faLanguage, tone: "lp6-ic-blue" },
  { n: 4, icon: faUsers, tone: "lp6-ic-cyan" },
];

// How it works — the believable path to the dream outcome.
const STEPS = [
  { n: 1, icon: faLightbulb, tone: "lp6-ic-cyan" },
  { n: 2, icon: faRobot, tone: "lp6-ic-purple" },
  { n: 3, icon: faHeadphones, tone: "lp6-ic-blue" },
  { n: 4, icon: faRocket, tone: "lp6-ic-cyan" },
];


const LandingPage6 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Transparent navbar over the hero, fading to solid navy on scroll. Body
  // classes are cleaned up on unmount so every other page keeps the solid one.
  useEffect(() => {
    document.body.classList.add("lp6-active");
    const onScroll = () => {
      document.body.classList.toggle("lp6-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp6-active", "lp6-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const goEnroll = () => navigate("/subscriptions");
  const scrollToOffer = () => {
    const el = document.getElementById("lp6-offer");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp6">
        {/* ----------------------------- HERO ----------------------------- */}
        <section className="lp6-hero">
          <span className="lp6-orb lp6-orb-1" />
          <span className="lp6-orb lp6-orb-2" />
          <span className="lp6-orb lp6-orb-3" />

          <Container>
            <Row>
              <Col lg="7">
                <div className="lp6-glass">
                  <span className="lp6-eyebrow">
                    <span className="dot" />
                    {t("lp6.eyebrow")}
                  </span>
                  <h1 className="lp6-title">{t("lp6.heroTitle")}</h1>
                  <p className="lp6-lead">{t("lp6.heroLead")}</p>

                  <div className="lp6-actions">
                    <button className="lp6-btn-primary" onClick={goEnroll}>
                      {t("lp6.heroPrimary")}
                    </button>
                    <button className="lp6-btn-ghost" onClick={scrollToOffer}>
                      {t("lp6.heroSecondary")}
                    </button>
                  </div>

                  <div className="lp6-pills">
                    <span className="lp6-pill">{t("lp6.pill1")}</span>
                    <span className="lp6-pill">{t("lp6.pill2")}</span>
                    <span className="lp6-pill">{t("lp6.pill3")}</span>
                  </div>
                </div>
              </Col>

              <Col lg="5">
                <div className="lp6-preview-wrap">
                  <div className="lp6-preview">
                    <div className="lp6-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="lp6-code">
                      <div className="lp6-code-line">
                        <div className="lp6-bar cyan" style={{ width: "34%" }} />
                        <div className="lp6-bar mute" style={{ width: "24%" }} />
                      </div>
                      <div className="lp6-code-line">
                        <div className="lp6-bar purple" style={{ width: "20%" }} />
                        <div className="lp6-bar mute" style={{ width: "44%" }} />
                      </div>
                      <div className="lp6-code-line">
                        <div className="lp6-bar dim" style={{ width: "56%" }} />
                      </div>
                    </div>
                    <div className="lp6-bubble">
                      <div className="lp6-avatar" />
                      <div className="lp6-bubble-lines">
                        <div className="lp6-bar" style={{ width: "90%" }} />
                        <div className="lp6-bar" style={{ width: "68%" }} />
                      </div>
                    </div>
                    <span className="lp6-ask">{t("lp6.previewAsk")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- PROBLEM ---------------------------- */}
        <section className="lp6-problem">
          <Container>
            <div className="lp6-section-head lp6-head-dark">
              <h2>{t("lp6.problemTitle")}</h2>
              <p>{t("lp6.problemSubtitle")}</p>
            </div>
            <Row>
              {PROBLEMS.map((p) => (
                <Col lg="3" md="6" className="lp6-col" key={p.n}>
                  <div className="lp6-card lp6-card-dark">
                    <div className={`lp6-card-icon ${p.tone}`}>
                      <FontAwesomeIcon icon={p.icon} />
                    </div>
                    <h6>{t(`lp6.problem${p.n}T`)}</h6>
                    <p>{t(`lp6.problem${p.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ------------------------- HOW IT WORKS ------------------------- */}
        <section className="lp6-how">
          <Container>
            <div className="lp6-section-head">
              <h2>{t("lp6.howTitle")}</h2>
              <p>{t("lp6.howSubtitle")}</p>
            </div>
            <Row>
              {STEPS.map((s, i) => (
                <Col lg="3" md="6" className="lp6-col" key={s.n}>
                  <div className="lp6-step">
                    <span className="lp6-step-num">{i + 1}</span>
                    <div className={`lp6-card-icon ${s.tone}`}>
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                    <h6>{t(`lp6.how${s.n}T`)}</h6>
                    <p>{t(`lp6.how${s.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* --------------------- OFFER / WHAT'S INCLUDED ------------------- */}
        <section className="lp6-offer" id="lp6-offer">
          <Container>
            <div className="lp6-section-head">
              <span className="lp6-eyebrow-2">{t("lp6.stackEyebrow")}</span>
              <h2>{t("lp6.stackTitle")}</h2>
              <p>{t("lp6.stackSubtitle")}</p>
            </div>

            <Row className="lp6-offer-grid">
              <ValueStack prefix="lp6" />

              <Col lg="5">
                <div className="lp6-price-card">
                  <span className="lp6-price-eyebrow">
                    {t("lp6.priceEyebrow")}
                  </span>
                  <h3 className="lp6-price-title">{t("lp6.priceTitle")}</h3>
                  <p className="lp6-price-sub">{t("lp6.priceSubtitle")}</p>

                  <div className="lp6-price-amount">
                    {/* The struck-through price is the value-stack total, so it
                        only makes sense while the stack is on the page. With the
                        stack shelved, the comparison line below carries the
                        anchor instead. */}
                    {SHOW_STACK_PRICES && (
                      <span className="lp6-price-strike">
                        {t("lp6.stackTotalValue")}
                      </span>
                    )}
                    <span className="lp6-price-now">{t("lp6.priceAmount")}</span>
                  </div>
                  <span className="lp6-price-plan">{t("lp6.pricePlan")}</span>
                  {!SHOW_STACK_PRICES && (
                    <span className="lp6-price-compare">
                      {t("lp6.priceCompare")}
                    </span>
                  )}

                  <button
                    className="lp6-btn-primary lp6-price-btn"
                    onClick={goEnroll}
                  >
                    {/* Don't promise a seat we can't sell yet — while enrollment
                        is closed this reads "Coming soon" and the offer page it
                        opens explains why. */}
                    {ENROLLMENT_OPEN
                      ? t("lp6.priceCta")
                      : t("enrollment.comingSoonCta")}
                  </button>

                  <div className="lp6-price-meta">
                    <span>
                      <FontAwesomeIcon icon={faUsers} /> {t("lp6.priceSeats")}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faClock} /> {t("lp6.priceStart")}
                    </span>
                  </div>

                  <div className="lp6-price-guarantee">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <span>{t("lp6.guaranteeTitle")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- GUARANTEE -------------------------- */}
        <section className="lp6-guarantee">
          <Container>
            <div className="lp6-guarantee-card">
              <div className="lp6-guarantee-badge">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <span className="lp6-eyebrow-2">
                  {t("lp6.guaranteeEyebrow")}
                </span>
                <h3>{t("lp6.guaranteeTitle")}</h3>
                <p>{t("lp6.guaranteeBody")}</p>
              </div>
            </div>
          </Container>
        </section>

        {/* --------------------------- FINAL CTA -------------------------- */}
        <section className="lp6-cta">
          <Container>
            <div className="lp6-cta-card">
              <Row>
                <Col lg="8">
                  <h3>{t("lp6.finalTitle")}</h3>
                  <p>{t("lp6.finalBody")}</p>
                </Col>
                <Col lg="4" className="ml-lg-auto">
                  <button className="lp6-cta-btn" onClick={goEnroll}>
                    {t("lp6.finalCta")}
                  </button>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default LandingPage6;
